import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  IconButton,
  styled,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
  Skeleton,
} from "@mui/material";
import { TiVideo } from "react-icons/ti";
import { HiPhone } from "react-icons/hi2";
import { IoDocumentAttach } from "react-icons/io5";
import CloseIcon from "@mui/icons-material/Close";
import ChatPlaceholder from "./ChatPlaceholder";
import VideoCallPopup from "./GroupVideoCall";
import { SlCallIn } from "react-icons/sl";
import { HiOutlinePhoneMissedCall } from "react-icons/hi";
import { IoIosSend } from "react-icons/io";
import { useTranslation } from "react-i18next";
import Linkify from "react-linkify";
import useFetchUser from "hooks/useFetchUser";
import camIcon from "../../assets/img/cam.svg";
import { CallersIdContext } from "store/callersIdContext";
import { VideoCallContext } from "store/VideoCallContext";
import colors from "colors/color";
import sendIcon from "../../assets/img/send_icon.svg";

const CompanyLogo = styled(Box)(({ theme, logo }) => ({
  overflow: "hidden",
  minWidth: "48px",
  minHeight: "48px",
  maxWidth: "48px",
  maxHeight: "48px",
  background:
    "linear-gradient(135deg, rgba(0, 23, 231, 0.5) -54.69%, rgba(255, 204, 248, 0.5) 66.41%)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 14,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#fff",
  fontSize: "0.75rem",
  textAlign: "center",
}));

// Add MessageSkeleton component at the top level
const MessageSkeleton = ({ isOwnMessage }) => (
  <Box
    sx={{
      display: "flex",
      justifyContent: isOwnMessage ? "flex-end" : "flex-start",
      mb: 2,
      width: "100%",
    }}
  >
    {!isOwnMessage && (
      <Skeleton variant="circular" width={32} height={32} sx={{ mr: 1 }} />
    )}
    <Box
      sx={{
        maxWidth: "80%",
        bgcolor: isOwnMessage ? "#0084FF" : "#F8F9FA",
        borderRadius: isOwnMessage ? "20px 0 20px 20px" : "0 20px 20px 20px",
        p: 2,
      }}
    >
      <Skeleton
        variant="text"
        width={Math.random() * 100 + 100}
        height={20}
        sx={{
          bgcolor: isOwnMessage ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.1)",
        }}
      />
      <Skeleton
        variant="text"
        width={40}
        height={12}
        sx={{
          mt: 1,
          bgcolor: isOwnMessage ? "rgba(255,255,255,0.3)" : "rgba(0,0,0,0.1)",
        }}
      />
    </Box>
  </Box>
);

const ChatComponent = ({
  display,
  userInfo,
  senderId,
  receiverId,
  message,
  setMessage,
  messagesHistory,
  setMessagesHistory,
  messages,
  handleMessageSend,
  handleFileUpload,
  handleFileChange,
  handleFileRemove,
  fileName,
  renderMessage,
  historyRef,
  // Video call props
  callStatus,
  callTypeRef,
  startLocalStream,
  startLocalAudioStream,
  acceptCall,
  endCall,
  isScreenSharing,
  toggleScreenShare,
  ringAudioRef,
  signalingServer,
  receiverData,
  handleOpenDialog,
  callData,
  setCallData,
  // callers
}) => {
  const apiUrl = process.env.REACT_APP_API_URL;
  const historyBoxRef = useRef(null);
  const [messageToSend, setMessageToSend] = useState("");
  const theme = useTheme();
  const isDownThanLg = useMediaQuery(theme.breakpoints.down("lg"));
  const { t } = useTranslation();
  const { formData } = useFetchUser();
  const { callers, setCallers } = useContext(CallersIdContext);
  const { videoCallLaunched, setVideoCallLaunched } =
    useContext(VideoCallContext);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const PAGE_SIZE = 10;
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Function to fetch more messages when scrolling up
  const fetchMoreMessages = async () => {
    if (loading || !hasMore) {
      console.log("Skipping fetch - loading:", loading, "hasMore:", hasMore);
      return;
    }

    console.log("Fetching more messages - page:", page);
    setLoading(true);
    try {
      const nextPage = page + 1;
      const url = `${apiUrl}messaging/api/chat/${receiverId}/${senderId}/history/?page=${nextPage}&page_size=${PAGE_SIZE}`;
      console.log("Fetching from URL:", url);

      const response = await fetch(url);

      // Log response headers for debugging
      console.log("Response status:", response.status);
      console.log("Total count:", response.headers.get("X-Total-Count"));
      console.log("Current page:", response.headers.get("X-Page"));

      if (!response.ok) throw new Error("Failed to fetch messages");

      const newMessages = await response.json();
      console.log("Received messages:", newMessages.length);

      if (newMessages.length < PAGE_SIZE) {
        console.log("No more messages to load");
        setHasMore(false);
      }

      if (newMessages.length > 0) {
        // Calculate current scroll position and height before adding new messages
        const historyBox = historyBoxRef.current;
        const oldScrollHeight = historyBox ? historyBox.scrollHeight : 0;
        const scrollPosition = historyBox ? historyBox.scrollTop : 0;

        setPage(nextPage);
        // Prepend new messages to the existing history and ensure they're sorted by timestamp
        setMessagesHistory((prev) => {
          const combinedMessages = [...newMessages, ...prev];

          // Sort messages by timestamp in chronological order
          return combinedMessages.sort(
            (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
          );
        });
        console.log("Updated messages history with new messages");

        // After rendering new messages, adjust scroll position to maintain relative position
        setTimeout(() => {
          if (historyBox) {
            const newScrollHeight = historyBox.scrollHeight;
            const heightDifference = newScrollHeight - oldScrollHeight;
            historyBox.scrollTop = scrollPosition + heightDifference;
            console.log(
              "Adjusted scroll position:",
              scrollPosition + heightDifference
            );
          }
        }, 50); // Increased timeout for better reliability
      } else {
        console.log("No new messages received");
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching more messages:", error);
    } finally {
      setLoading(false);
    }
  };

  // Handle scroll event to load more messages when near the top
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // console.log("Scroll event - scrollTop:", scrollTop, "scrollHeight:", scrollHeight, "clientHeight:", clientHeight);

    // If we're near the top (within 100px) and not currently loading and there might be more messages
    if (scrollTop < 100 && !loading && hasMore) {
      // console.log("Scroll threshold reached, triggering fetchMoreMessages");
      fetchMoreMessages();
    }
  };

  // Add scroll event listener
  useEffect(() => {
    const historyBox = historyBoxRef.current;
    if (historyBox) {
      console.log("Adding scroll event listener");
      historyBox.addEventListener("scroll", handleScroll);
      return () => {
        console.log("Removing scroll event listener");
        historyBox.removeEventListener("scroll", handleScroll);
      };
    }
  }, [loading, hasMore, receiverId, page]); // Added page to dependencies

  // Reset pagination when changing conversations
  useEffect(() => {
    console.log("Conversation changed, resetting pagination state");
    setPage(1);
    setHasMore(true);
  }, [receiverId]);

  useEffect(() => {
    callers.forEach((caller) => {
      console.log("display caller:", caller.callerId);
    });
  }, [callers]);

  useEffect(() => {
    console.log("userInfo:", userInfo);
  }, [userInfo]);
  useEffect(() => {
    const userName = `${formData.first_name} ${formData.last_name}`;
    localStorage.setItem("username", userName);
    // localStorage.setItem('resume',formData.cv);
  }, [formData]);
  useEffect(() => {
    setMessageToSend(message || "");
  }, [message]);

  useEffect(() => {
    setMessage(messageToSend || "");
  }, [messageToSend, setMessage]);

  useEffect(() => {
    setMessage("");
  }, [receiverId, setMessage]);

  useEffect(() => {
    // Only attempt to scroll if the ref is available and the component is displayed
    if (historyBoxRef.current && display) {
      historyBoxRef.current.scrollTop = historyBoxRef.current.scrollHeight;
    }
  }, [messagesHistory, messages, display]);

  // Add a new effect to ensure messages are always sorted
  useEffect(() => {
    if (messagesHistory.length > 0) {
      // Check if messages need sorting (first message timestamp > last message timestamp)
      const needsSorting =
        new Date(messagesHistory[0].timestamp) >
        new Date(messagesHistory[messagesHistory.length - 1].timestamp);

      if (needsSorting) {
        console.log("Sorting message history to ensure chronological order");
        const sortedMessages = [...messagesHistory].sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
        );

        setMessagesHistory(sortedMessages);
      }
    }
  }, [messagesHistory.length]);

  // Modify the useEffect that handles messagesHistory
  useEffect(() => {
    if (messagesHistory.length > 0) {
      setIsInitialLoading(false);
    }
  }, [messagesHistory]);

  const sendMessageHandler = () => {
    const messageSent = handleMessageSend();
    handleFileUpload();
    if (messageSent) {
      setMessageToSend("");
    }
  };
  function breakLongWords(text, maxLength = 20) {
    const regex = new RegExp(`.{1,${maxLength}}`, "g");
    return text.replace(/\S{20,}/g, (word) => word.match(regex).join(" "));
  }

  const handleKeyPress = (event) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (messageToSend.trim() !== "") {
        sendMessageHandler();
      }
    }
  };
  useEffect(() => {
    console.log("call data:", callData);
  }, [callData]);

  if (!display && !videoCallLaunched && !userInfo) {
    return <ChatPlaceholder display={display} />;
  }

  return (
    <Box
      sx={{
        height: "100%",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        overflow: "auto",
        border: "1px solid #D9D9D9",
        borderRadius: "20px",
        "&::-webkit-scrollbar": {
          width: "8px",
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: "transparent",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "linear-gradient(45deg, #0085FF, #004F98)",
          borderRadius: "4px",
          boxShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "linear-gradient(45deg, #004F98, #002B5C)",
        },
      }}
    >
      {/* Header */}
      {display && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            paddingRight: 4,
            bgcolor: "#F7F7F733",
            border: "1px solid #D9D9D9",
          }}
        >
          {/* User Info */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            {/* <Avatar>
              <Box
                sx={{
                  width: "100%",
                  height: "100%",
                  borderRadius: "50px",
                  bgcolor: "#0084FF",
                  color: "white",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  overflow: "hidden",
                }}
              >
                <img
                  src={userInfo.image}
                  alt="User"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                />
              </Box>
            </Avatar> */}
            <CompanyLogo
              logo={
                userInfo?.image || userInfo?.company || userInfo?.first_name
              }
            >
              {userInfo?.image ? (
                <img src={userInfo.image} alt="Company Logo" />
              ) : (
                <Typography
                  sx={{
                    color: "white",
                    textAlign: "center",
                    fontFamily: "Plain",
                    fontWeight: 600,
                    fontSize: "4.88px",
                    lineHeight: "100%",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",
                  }}
                >
                  {userInfo?.company ||
                    userInfo?.first_name?.[0]?.toUpperCase() ||
                    ""}
                </Typography>
              )}
            </CompanyLogo>
            <Box>
              <Typography
                variant="h6"
                sx={{
                  fontFamily: "Poppins",
                  fontWeight: 500,
                  fontSize: "16px",
                  lineHeight: "100%",
                  color: colors.primary,
                  textTransform: "none",
                }}
              >
                {userInfo?.first_name
                  ? userInfo.first_name.charAt(0).toUpperCase() +
                  userInfo.first_name.slice(1)
                  : ""}{" "}
                {userInfo?.last_name
                  ? userInfo.last_name.charAt(0).toUpperCase() +
                  userInfo.last_name.slice(1)
                  : ""}
              </Typography>

              {userInfo?.company && (
                <Typography
                  sx={{
                    fontFamily: "Poppins",
                    fontWeight: 400,
                    fontSize: "12px",
                    color: "#00000080",
                    paddingTop: 0.5,
                  }}
                >
                  {userInfo.company}
                </Typography>
              )}
            </Box>
          </Box>
          {/* Call Controls */}
          {!userInfo ? null : videoCallLaunched ||
            userInfo.role !== "c" ? null : (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Box
                sx={{
                  width: "30px",
                  height: "30px",
                  border: "1px solid #0085FF",
                  borderRadius: "10px",
                  bgcolor: "#fff",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  boxShadow: "0 4px 40px 0 #00000014",
                }}
              >
                {/* {videoCallLaunched  ? null : ( */}
                <Button
                  variant="contained"
                  onClick={() => {
                    // if( callData){
                    if (
                      !callers.some(
                        (caller) =>
                          caller.callerId === userInfo.id ||
                          caller.roomId === callData.meetingLink
                      )
                    ) {
                      handleOpenDialog();
                      // }
                    } else {
                      setVideoCallLaunched(true);
                    }
                  }}
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    px: 1,
                    py: 1.5,
                    backgroundColor: callers.some(
                      (caller) => caller.callerId === userInfo.id
                    )
                      ? "primary.main"
                      : "#fff",
                    color: callers.some(
                      (caller) => caller.callerId === userInfo.id
                    )
                      ? "#fff"
                      : "text.primary",
                    border: callers.some(
                      (caller) => caller.callerId === userInfo.id
                    )
                      ? "none"
                      : "1px solid #e0e0e0",
                    "&:hover": {
                      backgroundColor: callers.some(
                        (caller) => caller.callerId === userInfo.id
                      )
                        ? "primary.dark"
                        : "#f5f5f5",
                      boxShadow: "none",
                    },
                    display: "flex",
                    boxShadow: "none",
                    // gap: 1,
                    alignItems: "center",
                  }}
                >
                  {callers.some((caller) => caller.callerId === userInfo.id) ? (
                    "join meeting"
                  ) : (
                    <img
                      src={camIcon}
                      alt="camera"
                      style={{ width: 12, height: 12 }}
                    />
                  )}
                </Button>
                {/* )} */}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Messages Container */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          px: 4,
          py: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          width: "100%",
          overflow: "auto",
          "&::-webkit-scrollbar": {
            width: "8px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "linear-gradient(45deg, #0085FF, #004F98)",
            borderRadius: "4px",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.3)",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "linear-gradient(45deg, #004F98, #002B5C)",
          },
        }}
        ref={historyBoxRef}
      >
        {/* Loading indicator for pagination */}
        {loading && (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <Typography
              sx={{
                color: colors.primary,
                fontFamily: "Poppins",
                fontSize: "14px",
              }}
            >
              Loading more messages...
            </Typography>
          </Box>
        )}

        {/* Video Call Container */}
        {display && callStatus && (
          <Box className="video-call-container">
            {callStatus === "ringing" && (
              <Box sx={{ display: "flex", gap: 2 }}>
                <IconButton onClick={acceptCall}>
                  <SlCallIn />
                </IconButton>
                <IconButton onClick={endCall}>
                  <HiOutlinePhoneMissedCall />
                </IconButton>
              </Box>
            )}

            {callStatus === "active" && (
              <Box className="call-controls" sx={{ display: "flex", gap: 2 }}>
                <IconButton onClick={endCall}>
                  <img
                    style={{ width: "30px", height: "30px" }}
                    src="https://res.cloudinary.com/dwckvxlca/image/upload/v1726679832/kk4vhf2u9axne003xixs.png"
                    alt="End"
                  />
                </IconButton>
                {callTypeRef?.current === "video" && (
                  <IconButton onClick={toggleScreenShare}>
                    <img
                      style={{ width: "30px", height: "30px" }}
                      src={
                        isScreenSharing
                          ? "https://res.cloudinary.com/dwckvxlca/image/upload/v1726679906/nl2bfcmhue28znzrtlta.png"
                          : "https://res.cloudinary.com/dwckvxlca/image/upload/v1726679942/zvxhmbpojgqrpwb8w8xq.png"
                      }
                      alt="Screen Share"
                    />
                  </IconButton>
                )}
              </Box>
            )}
          </Box>
        )}

        {/* Messages */}
        <Box ref={historyRef}>
          {isInitialLoading ? (
            <>
              {[...Array(5)].map((_, index) => (
                <MessageSkeleton key={index} isOwnMessage={index % 2 === 0} />
              ))}
            </>
          ) : (
            <>
              {/* History Messages */}
              {messagesHistory.map((msg, index) => (
                <Box
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent:
                      msg.sender == senderId ? "flex-end" : "flex-start",
                    mb: 2,
                    width: "100%",
                  }}
                >
                  {msg.sender != senderId && (
                    <CompanyLogo sx={{ mr: 1, width: 32, height: 32 }}>
                      {userInfo?.image ? (
                        <img
                          src={userInfo.image}
                          alt="User"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover",
                          }}
                        />
                      ) : (
                        userInfo?.company?.[0]?.toUpperCase() ||
                        userInfo?.last_name?.[0]?.toUpperCase() ||
                        ""
                      )}
                    </CompanyLogo>
                  )}
                  <Box
                    sx={{
                      maxWidth: "80%",
                      bgcolor: msg.sender == senderId ? "#0084FF" : "#F8F9FA",
                      color: msg.sender == senderId ? "white" : "black",
                      borderTopLeftRadius:
                        msg.sender == senderId ? "20px" : "0px",
                      borderBottomLeftRadius: "20px",
                      borderBottomRightRadius: "20px",
                      borderTopRightRadius:
                        msg.sender == senderId ? "0px" : "20px",
                      p: 2,
                      fontFamily: "Poppins",
                      whiteSpace: "pre-wrap",
                      overflowWrap: "break-word",
                    }}
                  >
                    <Linkify>{renderMessage(msg)}</Linkify>
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        color:
                          msg.sender == senderId
                            ? "rgba(255,255,255,0.7)"
                            : "#666",
                        mt: 1,
                      }}
                    >
                      {new Date(msg.timestamp).toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </Typography>
                  </Box>
                </Box>
              ))}

              {/* New Messages Section */}
              {messages.length > 0 && (
                <Box
                  sx={{
                    textAlign: "center",
                    color: "#0084FF",
                    fontSize: "8px",
                    my: 3,
                    py: 1,
                    borderRadius: 1,
                    background: "#F7FBFF",
                    textTransform: "uppercase",
                    letterSpacing: "0.5px",
                    fontFamily: "Poppins",
                  }}
                >
                  {t("newMessages")}
                </Box>
              )}

              {/* New Messages */}
              {messages
                .filter(
                  (msg) =>
                    (msg.recipient == parseInt(receiverId) &&
                      msg.sender == parseInt(senderId)) ||
                    (msg.recipient == parseInt(senderId) &&
                      msg.sender == parseInt(receiverId))
                )
                .map((msg, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      justifyContent:
                        msg.sender == senderId ? "flex-end" : "flex-start",
                      mb: 2,
                      width: "100%",
                    }}
                  >
                    {msg.sender != senderId && (
                      <CompanyLogo
                        logo={
                          userInfo?.image ||
                          userInfo?.company ||
                          userInfo?.first_name
                        }
                      >
                        {userInfo?.image ? (
                          <img src={userInfo.image} alt="Company Logo" />
                        ) : (
                          <Typography
                            sx={{
                              color: "white",
                              textAlign: "center",
                              fontFamily: "Plain",
                              fontWeight: 600,
                              fontSize: "4.88px",
                              lineHeight: "100%",
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              maxWidth: "100%",
                            }}
                          >
                            {userInfo?.company ||
                              userInfo?.first_name?.[0]?.toUpperCase() ||
                              ""}
                          </Typography>
                        )}
                      </CompanyLogo>
                    )}
                    <Box
                      sx={{
                        maxWidth: "80%",
                        bgcolor: msg.sender == senderId ? "#0084FF" : "#F8F9FA",
                        color: msg.sender == senderId ? "white" : "black",
                        // borderRadius: "20px",
                        borderTopLeftRadius:
                          msg.sender == senderId ? "20px" : "0px",
                        borderBottomLeftRadius: "20px",
                        borderBottomRightRadius: "20px",
                        borderTopRightRadius:
                          msg.sender == senderId ? "0px" : "20px",
                        p: 2,
                        fontFamily: "Poppins",
                        whiteSpace: "pre-wrap",
                        overflowWrap: "break-word",
                      }}
                    >
                      <Linkify>{renderMessage(msg)}</Linkify>

                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color:
                            msg.sender == senderId
                              ? "rgba(255,255,255,0.7)"
                              : "#666",
                          mt: 1,
                        }}
                      >
                        {new Date(msg.timestamp).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                          hour12: true,
                        })}
                      </Typography>
                    </Box>
                  </Box>
                ))}
            </>
          )}
        </Box>
      </Box>

      {/* Message Input */}
      {display && (
        <Box
          sx={{
            p: 2,
            width: "100%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              p: "4px 4px 4px 16px",
              width: "100%",
            }}
          >
            {/* File Attachment Button */}
            <Box
              sx={{
                width: "50px",
                height: "50px",
                border: `1px solid ${colors.primary}`,
                borderRadius: "10px",
                bgcolor: "#fff",
                overflow: "hidden",
                position: "relative",
              }}
            >
              <IconButton
                component="label"
                sx={{
                  color: colors.primary,
                  padding: 0,
                  width: "100%",
                  height: "100%",
                  borderRadius: "10px",
                  "&:hover": {
                    bgcolor: "rgba(0, 133, 255, 0.1)",
                  },
                }}
              >
                <IoDocumentAttach size={17} />
                <input
                  type="file"
                  hidden
                  onChange={handleFileChange}
                  accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                />
              </IconButton>
            </Box>

            {/* Message Input Box */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                bgcolor: "#F8F9FA",
                borderRadius: "10px",
                p: "4px 4px 4px 16px",
                width: "85%",
                height: "50px",
                border: "1px solid #D9D9D9",
                "&:hover": {
                  border: `1px solid ${colors.primary}`,
                },
                "&:focus-within": {
                  border: `1px solid ${colors.primary}`,
                },
                ":&:active": {
                  border: `1px solid ${colors.primary}`,
                },
              }}
            >
              {/* File Name Display */}
              {fileName && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    bgcolor: "#0085FF",
                    borderRadius: "4px",
                    py: 0.5,
                    px: 1.5,
                    maxWidth: "40%",
                    mr: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      color: "white",
                      maxWidth: "calc(100% - 24px)",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      fontFamily: "Poppins",
                    }}
                  >
                    {fileName}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={handleFileRemove}
                    sx={{
                      color: "white",
                      p: 0.25,
                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.2)",
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </Box>
              )}

              {/* Text Input */}
              <TextField
                fullWidth
                value={messageToSend}
                onChange={(e) => setMessageToSend(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder={t("typeYourMessage")}
                variant="standard"
                sx={{
                  "& .MuiInput-root": {
                    fontFamily: "Poppins",
                    fontSize: "14px",
                    "&:before, &:after": { display: "none" },
                  },
                }}
              />
            </Box>

            {/* Send Button */}
            <Box
              sx={{
                width: "10%",
                height: "50px",
                border: "1px solid ",
                borderColor: colors.primary,
                borderRadius: "10px",
                display: "flex",
                minWidth: "50px", // Ensure minimum width for icon
                maxWidth: "120px", // Maximum width before hiding text
                overflow: "hidden", // Prevent text from overflowing
              }}
            >
              <IconButton
                onClick={sendMessageHandler}
                sx={{
                  bgcolor: "#fff",
                  color: colors.primary,
                  fontSize: isDownThanLg ? "2.5rem" : "1.1rem",
                  "&:hover": {
                    bgcolor: "rgba(0, 133, 255, 0.1)",
                  },
                  "&.Mui-disabled": { bgcolor: "#CCE4FF" },
                  width: "100%",
                  height: "100%",
                  borderRadius: "inherit",
                  m: 0,
                  p: 0,
                  whiteSpace: "nowrap",
                }}
              >
                <img src={sendIcon} height={13} width={13} />
                {!isDownThanLg && (
                  <Typography
                    sx={{
                      marginLeft: 0.5,
                      fontFamily: "Poppins",
                      fontWeight: 300,
                      fontSize: "12px",
                      lineHeight: "100%",
                      display: { xs: "none", sm: "block" }, // Hide text on small screens
                      overflow: "hidden", // Hide overflow text
                      textOverflow: "ellipsis", // Add ellipsis for overflow
                    }}
                  >
                    {t("send")}
                  </Typography>
                )}
              </IconButton>
            </Box>
          </Box>
        </Box>
      )}

      {/* Ring Audio */}
      {ringAudioRef && (
        <audio
          ref={ringAudioRef}
          src="./assets/img/messenger_video_call.mp3"
          preload="auto"
          loop
        >
          Your browser does not support the audio element.
        </audio>
      )}
    </Box>
  );
};

export default ChatComponent;
